import {
  FormulaSet,
  MediaField,
  MediaFieldType,
  FormulaOperand,
  FormulaOperator,
} from "routes/Base/Admin/GlobalMediaTypes/types"
import {
  getMediaFieldBySemantic,
  getMediaFieldDaily,
  getMediaFieldValue,
  operatorToFunction,
  roundHalfEven,
  setMediaFieldValue,
} from "./utils"

import { ChangedPlanRecord, Status } from "../../types"
import { MediaFields } from "./types"
import { calculationContext } from "./calculations"
import { DEFAULT_PRECISION } from "providers/A12n"
import { FINANCIAL_FIELDS_SEMANTIC_CODES } from "./constants"

export function calculateFormulas(li: ChangedPlanRecord, precision: number) {
  const cc = calculationContext
  const media = cc.media
  if (!media) return li

  const calculatedFields = media.mediaFields?.filter(
    (mf) => mf.type === "Calculated",
  )

  if (!calculatedFields.length) return li

  // tracks the status of each calculated field whether there is a dependency to another calclucated field or not
  const statusMap: { [id: string]: Status } = {}
  calculatedFields.forEach((item) => {
    statusMap[item.id] = "UNRESOLVED"
  })

  let calculated = li
  while (Object.values(statusMap).some((v) => v !== "RESOLVED")) {
    calculated = calculatedFields.reduce((li, field) => {
      const fields = li[cc.fieldsKey]

      // in case of a package line do not compute gross cost
      const isGrossCost = field.semanticCode === "grossCost"
      const isPackage = li.type === "package"
      if (isGrossCost && isPackage) {
        statusMap[field.id] = "RESOLVED"
        return li
      }

      // in case the field is already resolved do not compute it again
      if (statusMap[field.id] === "RESOLVED") return li

      const updated = calculateFormula(
        media.mediaFields,
        fields,
        field,
        precision,
        statusMap,
      )

      if (!updated) {
        statusMap[field.id] = "RESOLVED"
        return li
      }

      return { ...li, [cc.fieldsKey]: { ...li[cc.fieldsKey], ...updated } }
    }, calculated)
  }
  return calculated
}

export function calculateFormula(
  mediaFields: MediaField[],
  fields: MediaFields,
  field: MediaField,
  precision: number = DEFAULT_PRECISION,
  statusMap: { [id: string]: Status } = {},
) {
  if (!fields) return

  const formula = field.formulaSet
  if (!formula) return

  const parts = [...formula.formulaParts]
  const partsShifted = parts?.map((p, pidx) => ({
    ...p,
    partOperator: parts?.[pidx - 1]?.partOperator,
  }))

  let shouldDrop = false
  let hasDependencies = false

  const formulaValue = partsShifted.reduce((v: number | undefined, p) => {
    if (!p.leftOperand || !p.rightOperand) return v

    const partsOperator = operatorToFunction(p.partOperator)
    const operator = operatorToFunction(p.operator)
    if (!operator) return v

    const lField = mediaFields?.find(
      (mf) => mf.id === p.leftOperand?.mediaFieldId,
    )
    const rField = mediaFields?.find(
      (mf) => mf.id === p.rightOperand?.mediaFieldId,
    )

    // if the pair is VariablCost and SpotsPlan calculate daily
    const mustCalculateDaily = ["VariableCost", "SpotsPlan"].every((v) =>
      [lField?.type, rField?.type].includes(v as MediaFieldType),
    )

    if (mustCalculateDaily) {
      const spotsField = lField?.type === "SpotsPlan" ? lField : rField
      const variableField = lField?.type === "VariableCost" ? lField : rField

      const spots = getMediaFieldDaily(fields, spotsField)
      const variables = getMediaFieldDaily(fields, variableField)

      if (!spots || !variables) return undefined

      const partSum = Object.keys(spots)?.reduce((spotsSum, spotDate) => {
        const spotVariable = variables[spotDate] || variables["defaultCost"]
        const sum = roundHalfEven(
          operator(spotVariable, spots[spotDate]),
          precision,
        )
        return spotsSum + sum
      }, 0)
      const sum = roundHalfEven(partsOperator(v || 0, partSum), precision)
      return sum
    } else {
      if (
        (lField?.type === "Calculated" &&
          statusMap[lField.id] &&
          statusMap[lField.id] !== "RESOLVED") ||
        (rField?.type === "Calculated" &&
          statusMap[rField.id] &&
          statusMap[rField.id] !== "RESOLVED")
      ) {
        // we need to drop the calculation if at least one operand value is calculated and not resolved
        hasDependencies = true
        shouldDrop = true
        return v
      }

      /** We need to drop calculation if r or l field is financial and billingType if not set **/
      const billingTypeField = getMediaFieldValue<number>(
        fields,
        getMediaFieldBySemantic("billingType"),
      )
      if (
        !billingTypeField &&
        (FINANCIAL_FIELDS_SEMANTIC_CODES.includes(lField?.semanticCode || "") ||
          FINANCIAL_FIELDS_SEMANTIC_CODES.includes(rField?.semanticCode || ""))
      ) {
        shouldDrop = true
        return undefined
      }

      const left =
        p.leftOperand.fixedValue || getMediaFieldValue<number>(fields, lField)

      const right =
        p.rightOperand.fixedValue || getMediaFieldValue<number>(fields, rField)

      if (
        (!left && left !== 0) ||
        isNaN(left) ||
        (!right && right !== 0) ||
        isNaN(right)
      ) {
        // we need to drop the calculation if at least one operand value is undefined
        shouldDrop = true
        return v
      }

      const partSum = operator(Number(left), Number(right))
      const sum = partsOperator(v || 0, partSum)

      return sum
    }
  }, undefined)

  statusMap[field.id] = hasDependencies ? "PENDING" : "RESOLVED"

  const roundedValue =
    shouldDrop || (!formulaValue && formulaValue !== 0)
      ? undefined
      : roundHalfEven(formulaValue, precision)

  return setMediaFieldValue(roundedValue, fields, field)
}

export function stringifyFormulaSet(
  formulaSet: FormulaSet,
  mt: MediaField[],
): string {
  return formulaSet.formulaParts
    .reduce<string[]>(
      (previousValue, currentValue) => [
        ...previousValue,
        ...Object.entries(currentValue).reduce<string[]>(
          (previousValue1, [formulaPartName, equationPart]) => {
            if (equationPart === undefined) {
              return previousValue1
            }

            const operand = stringifyEquationPart(equationPart, mt)

            if (formulaPartName === "leftOperand") {
              return [operand, ...previousValue1]
            }

            return [...previousValue1, operand]
          },
          [],
        ),
      ],
      [],
    )
    .join(" ")
}

export function stringifyEquationPart(
  formulaOperand: FormulaOperand | FormulaOperator,
  mt: MediaField[],
): string {
  if (typeof formulaOperand !== "string") {
    if ("mediaFieldId" in formulaOperand) {
      const mediaFieldName = mt.find(
        (mediaField) => mediaField.id === formulaOperand.mediaFieldId,
      )?.name

      if (!mediaFieldName) {
        throw new Error("MediaFieldId not found!")
      }

      return mediaFieldName
    }
    if ("fixedValue" in formulaOperand) {
      return String(formulaOperand.fixedValue)
    }
  }

  return String(formulaOperand)
}
